import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import { Add, Edit, Delete, Person, Business, Security, Send, Refresh, Cancel, Settings, CloudUpload, CheckCircle, Error as ErrorIcon, ExpandMore, Notifications, NotificationsActive } from '@mui/icons-material'
import { PageHeader, Card, Button, DataTable, Modal, StatusChip } from '@components/common'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { toast } from 'react-toastify'

// User Management Tab
const UsersTab = () => {
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [inviteFormOpen, setInviteFormOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [activeSubTab, setActiveSubTab] = useState(0)
  const [editFormData, setEditFormData] = useState({
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_active: true,
    role_ids: [],
  })
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_staff: false,
    role_ids: [],
  })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, invitesRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/common/profiles/'),
        api.get('/auth/invitations/'),
        api.get('/common/departments/'),
        api.get('/common/roles/'),
      ])
      setUsers(usersRes.data.results || usersRes.data)
      setInvitations(invitesRes.data.results || invitesRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleInviteUser = () => {
    setInviteFormData({
      email: '',
      first_name: '',
      last_name: '',
      department: '',
      job_title: '',
      phone_number: '',
      is_technician: false,
      is_staff: false,
      role_ids: [],
    })
    setInviteFormOpen(true)
  }

  const handleEdit = (profile) => {
    setSelectedUser(profile)
    setEditFormData({
      department: profile.department || '',
      job_title: profile.job_title || '',
      phone_number: profile.phone_number || '',
      is_technician: profile.is_technician || false,
      is_active: profile.user?.is_active ?? true,
      role_ids: profile.roles?.map((r) => r.id) || [],
    })
    setEditFormOpen(true)
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      await api.patch(`/common/profiles/${selectedUser.id}/`, {
        department: editFormData.department || null,
        job_title: editFormData.job_title,
        phone_number: editFormData.phone_number,
        is_technician: editFormData.is_technician,
        is_active: editFormData.is_active,
        role_ids: editFormData.role_ids,
      })
      toast.success('User updated successfully')
      setEditFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to update user. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleSendInvitation = async () => {
    setSaving(true)
    try {
      await api.post('/auth/invitations/', {
        email: inviteFormData.email,
        first_name: inviteFormData.first_name,
        last_name: inviteFormData.last_name,
        department: inviteFormData.department || null,
        job_title: inviteFormData.job_title,
        phone_number: inviteFormData.phone_number,
        is_technician: inviteFormData.is_technician,
        is_staff: inviteFormData.is_staff,
        role_ids: inviteFormData.role_ids,
      })
      toast.success('Invitation sent successfully!')
      setInviteFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error sending invitation:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.email?.[0]
        || 'Failed to send invitation. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleResendInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/resend/`)
      toast.success('Invitation resent successfully!')
    } catch (error) {
      toast.error('Failed to resend invitation.')
    }
  }

  const handleCancelInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/cancel/`)
      toast.success('Invitation cancelled.')
      fetchData()
    } catch (error) {
      toast.error('Failed to cancel invitation.')
    }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/common/users/${selectedUser.user.id}/`)
      setDeleteModalOpen(false)
      toast.success('User deleted successfully')
      fetchData()
    } catch (error) {
      console.error('Error deleting user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to delete user. Please try again.'
      toast.error(errorMessage)
    }
  }

  const userColumns = [
    {
      field: 'user',
      headerName: 'Name',
      renderCell: (value) => `${value?.first_name || ''} ${value?.last_name || ''}`.trim() || value?.username,
    },
    { field: 'user', headerName: 'Username', renderCell: (value) => value?.username },
    { field: 'user', headerName: 'Email', renderCell: (value) => value?.email || '-' },
    { field: 'department_name', headerName: 'Department', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'is_technician',
      headerName: 'Technician',
      renderCell: (value) => value ? <Chip label="Yes" size="small" color="primary" /> : <Chip label="No" size="small" />,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      renderCell: (value) => value?.map((r) => r.name).join(', ') || '-',
    },
    {
      field: 'user',
      headerName: 'Status',
      renderCell: (value) => (
        <StatusChip status={value?.is_active ? 'active' : 'inactive'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setSelectedUser(row); setDeleteModalOpen(true) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const invitationColumns = [
    { field: 'email', headerName: 'Email' },
    {
      field: 'name',
      headerName: 'Name',
      renderCell: (_, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
    },
    { field: 'department_name', headerName: 'Department', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => (
        <Chip
          label={value}
          size="small"
          color={value === 'pending' ? 'warning' : value === 'accepted' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Sent',
      renderCell: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => row.status === 'pending' && (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            title="Resend invitation"
            onClick={(e) => { e.stopPropagation(); handleResendInvitation(row) }}
          >
            <Refresh fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Cancel invitation"
            onClick={(e) => { e.stopPropagation(); handleCancelInvitation(row) }}
          >
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={activeSubTab} onChange={(_, v) => setActiveSubTab(v)}>
          <Tab label={`Users (${users.length})`} />
          <Tab label={`Pending Invitations (${pendingInvitations.length})`} />
        </Tabs>
        <Button startIcon={<Send />} onClick={handleInviteUser}>Invite User</Button>
      </Box>

      {activeSubTab === 0 && (
        <DataTable
          columns={userColumns}
          data={users}
          loading={loading}
          searchPlaceholder="Search users..."
          emptyMessage="No users found"
        />
      )}

      {activeSubTab === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Users will receive an email invitation and must set their own password when accepting.
          </Alert>
          <DataTable
            columns={invitationColumns}
            data={pendingInvitations}
            loading={loading}
            searchPlaceholder="Search invitations..."
            emptyMessage="No pending invitations"
          />
        </>
      )}

      {/* Invite User Modal */}
      <Modal
        open={inviteFormOpen}
        onClose={() => setInviteFormOpen(false)}
        title="Invite New User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setInviteFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSendInvitation} loading={saving} startIcon={<Send />}>Send Invitation</Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          The user will receive an email with a link to set up their account and create their own password.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              required
              helperText="Invitation will be sent to this email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={inviteFormData.first_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={inviteFormData.last_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={inviteFormData.department}
                onChange={(e) => setInviteFormData({ ...inviteFormData, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={inviteFormData.job_title}
              onChange={(e) => setInviteFormData({ ...inviteFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={inviteFormData.phone_number}
              onChange={(e) => setInviteFormData({ ...inviteFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_technician}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={inviteFormData.role_ids}
                onChange={(e) => setInviteFormData({ ...inviteFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={inviteFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_staff}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_staff: e.target.checked })}
                />
              }
              label="Admin Access (Staff)"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        title="Edit User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} loading={saving}>Update</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={editFormData.department}
                onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={editFormData.job_title}
              onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={editFormData.phone_number}
              onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_technician}
                  onChange={(e) => setEditFormData({ ...editFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={editFormData.role_ids}
                onChange={(e) => setEditFormData({ ...editFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={editFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                  color={editFormData.is_active ? 'success' : 'default'}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Account Active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {editFormData.is_active
                      ? 'User can log in and access the system'
                      : 'User is blocked from logging in'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>Delete</Button>
          </>
        }
      >
        <Typography>Are you sure you want to delete "{selectedUser?.user?.username}"?</Typography>
      </Modal>
    </>
  )
}

// Department Management Tab
const DepartmentsTab = () => {
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedDept, setSelectedDept] = useState(null)
  const [formData, setFormData] = useState({ name: '', code: '', description: '', is_active: true })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      const response = await api.get('/common/departments/')
      setDepartments(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching departments:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedDept(null)
    setFormData({ name: '', code: '', description: '', is_active: true })
    setFormOpen(true)
  }

  const handleEdit = (dept) => {
    setSelectedDept(dept)
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      is_active: dept.is_active,
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedDept) {
        await api.patch(`/common/departments/${selectedDept.id}/`, formData)
        toast.success('Department updated successfully')
      } else {
        await api.post('/common/departments/', formData)
        toast.success('Department created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving department:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.code?.[0]
        || error.response?.data?.name?.[0]
        || 'Failed to save department. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'code', headerName: 'Code', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    { field: 'member_count', headerName: 'Members' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Department</Button>
      </Box>
      <DataTable
        columns={columns}
        data={departments}
        loading={loading}
        searchPlaceholder="Search departments..."
        emptyMessage="No departments found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedDept ? 'Edit Department' : 'Add Department'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedDept ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Department Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Roles Management Tab
const RolesTab = () => {
  const [roles, setRoles] = useState([])
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [permissionCategories, setPermissionCategories] = useState({})
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true, permissions: [] })
  const [saving, setSaving] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/common/roles/'),
        api.get('/auth/permissions/available/'),
      ])
      setRoles(rolesRes.data.results || rolesRes.data)
      setAvailablePermissions(permsRes.data.permissions || {})
      setPermissionCategories(permsRes.data.categories || {})
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedRole(null)
    setFormData({ name: '', description: '', is_active: true, permissions: [] })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleEdit = (role) => {
    setSelectedRole(role)
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active,
      permissions: role.permissions || [],
    })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedRole) {
        await api.patch(`/common/roles/${selectedRole.id}/`, formData)
        toast.success('Role updated successfully')
      } else {
        await api.post('/common/roles/', formData)
        toast.success('Role created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving role:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || 'Failed to save role. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const togglePermission = (permCode) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter((p) => p !== permCode)
        : [...prev.permissions, permCode],
    }))
  }

  const toggleCategory = (categoryPerms) => {
    const permCodes = categoryPerms.map((p) => p.code)
    const allSelected = permCodes.every((p) => formData.permissions.includes(p))
    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !permCodes.includes(p)),
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...permCodes])],
      }))
    }
  }

  const columns = [
    { field: 'name', headerName: 'Role Name', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    {
      field: 'permissions',
      headerName: 'Permissions',
      renderCell: (value) => (
        <Chip label={`${value?.length || 0} permissions`} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Role</Button>
      </Box>
      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        searchPlaceholder="Search roles..."
        emptyMessage="No roles found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedRole ? 'Edit Role' : 'Add Role'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedRole ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Permissions ({formData.permissions.length} selected)
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(permissionCategories).map(([category, perms]) => {
                const allSelected = perms.every((p) => formData.permissions.includes(p.code))
                const someSelected = perms.some((p) => formData.permissions.includes(p.code))
                return (
                  <Box key={category} mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleCategory(perms)}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedCategories((prev) => ({
                          ...prev,
                          [category]: !prev[category],
                        }))}
                      >
                        {category} ({perms.filter((p) => formData.permissions.includes(p.code)).length}/{perms.length})
                      </Typography>
                    </Box>
                    <Box pl={4}>
                      {perms.map((perm) => (
                        <FormControlLabel
                          key={perm.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.permissions.includes(perm.code)}
                              onChange={() => togglePermission(perm.code)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {perm.name} <Typography component="span" variant="caption" color="text.secondary">({perm.code})</Typography>
                            </Typography>
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Notification Settings Tab
const NotificationSettingsTab = () => {
  const [configsByCategory, setConfigsByCategory] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [users, setUsers] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [editFormData, setEditFormData] = useState({
    use_default_recipients: true,
    is_enabled: true,
    allow_push: true,
    allow_email: true,
    include_all_admins: false,
    include_all_technicians: false,
    additional_user_ids: [],
    additional_department_ids: [],
    additional_role_ids: [],
  })

  const fetchConfigs = async () => {
    try {
      setLoading(true)
      const response = await api.get('/common/notification-configs/by-category/')
      setConfigsByCategory(response.data)
    } catch (error) {
      console.error('Error fetching notification configs:', error)
      toast.error('Failed to load notification settings')
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [usersRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/common/profiles/'),
        api.get('/common/departments/'),
        api.get('/common/roles/'),
      ])
      setUsers(usersRes.data.results || usersRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  useEffect(() => {
    fetchConfigs()
    fetchOptions()
  }, [])

  const handleToggleEnabled = async (config, enabled) => {
    try {
      await api.patch(`/common/notification-configs/${config.id}/`, { is_enabled: enabled })
      fetchConfigs()
      toast.success(`${config.display_name} ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      toast.error('Failed to update notification setting')
    }
  }

  const handleEditConfig = async (config) => {
    try {
      // Fetch full config details
      const response = await api.get(`/common/notification-configs/${config.id}/`)
      const fullConfig = response.data
      setSelectedConfig(fullConfig)
      setEditFormData({
        use_default_recipients: fullConfig.use_default_recipients,
        is_enabled: fullConfig.is_enabled,
        allow_push: fullConfig.allow_push,
        allow_email: fullConfig.allow_email,
        include_all_admins: fullConfig.include_all_admins,
        include_all_technicians: fullConfig.include_all_technicians,
        additional_user_ids: fullConfig.additional_user_ids || [],
        additional_department_ids: fullConfig.additional_department_ids || [],
        additional_role_ids: fullConfig.additional_role_ids || [],
      })
      setEditModalOpen(true)
    } catch (error) {
      toast.error('Failed to load notification config')
    }
  }

  const handleSaveConfig = async () => {
    setSaving(true)
    try {
      await api.patch(`/common/notification-configs/${selectedConfig.id}/`, editFormData)
      toast.success('Notification settings updated')
      setEditModalOpen(false)
      fetchConfigs()
    } catch (error) {
      console.error('Error saving config:', error)
      toast.error('Failed to save notification settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        Configure which notifications are sent and who receives them. In-app notifications are always sent
        and cannot be disabled. You can add additional recipients beyond the default ones for each notification type.
      </Alert>

      {Object.entries(configsByCategory).map(([category, { label, configs }]) => (
        <Accordion key={category} defaultExpanded={category === 'work_orders' || category === 'fault_reports'}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <NotificationsActive fontSize="small" color="primary" />
              <Typography fontWeight="medium">{label}</Typography>
              <Chip
                label={`${configs.filter(c => c.is_enabled).length}/${configs.length} enabled`}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {configs.map((config) => (
                <Box
                  key={config.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                    bgcolor: config.is_enabled ? 'transparent' : 'action.hover',
                    opacity: config.is_enabled ? 1 : 0.7,
                  }}
                >
                  <Box flex={1}>
                    <Typography variant="body1" fontWeight="medium">
                      {config.display_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {config.description}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5}>
                      {config.use_default_recipients && (
                        <Chip
                          label={`Default: ${config.default_recipients_description}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {(config.additional_users_count > 0 || config.additional_departments_count > 0 || config.additional_roles_count > 0) && (
                        <Chip
                          label={`+${config.additional_users_count + config.additional_departments_count + config.additional_roles_count} additional`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      {config.include_all_admins && (
                        <Chip label="All admins" size="small" color="warning" variant="outlined" />
                      )}
                      {config.include_all_technicians && (
                        <Chip label="All technicians" size="small" color="info" variant="outlined" />
                      )}
                      {!config.allow_push && (
                        <Chip label="Push disabled" size="small" color="default" variant="outlined" />
                      )}
                      {!config.allow_email && (
                        <Chip label="Email disabled" size="small" color="default" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditConfig(config)}
                      title="Configure recipients"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <Switch
                      checked={config.is_enabled}
                      onChange={(e) => handleToggleEnabled(config, e.target.checked)}
                      color="primary"
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Edit Configuration Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={`Configure: ${selectedConfig?.display_name}`}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} loading={saving}>Save Changes</Button>
          </>
        }
      >
        {selectedConfig && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedConfig.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Enable/Disable and Channels */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.is_enabled}
                    onChange={(e) => setEditFormData({ ...editFormData, is_enabled: e.target.checked })}
                  />
                }
                label="Notification Enabled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.allow_push}
                      onChange={(e) => setEditFormData({ ...editFormData, allow_push: e.target.checked })}
                    />
                  }
                  label="Push"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.allow_email}
                      onChange={(e) => setEditFormData({ ...editFormData, allow_email: e.target.checked })}
                    />
                  }
                  label="Email"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                In-app notifications are always enabled
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Default Recipients
              </Typography>
            </Grid>

            {/* Default Recipients Toggle */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.use_default_recipients}
                      onChange={(e) => setEditFormData({ ...editFormData, use_default_recipients: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Use Default Recipients</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConfig.default_recipients_description}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Additional Recipients
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Add more users, departments, or roles to receive this notification (in addition to default recipients).
              </Typography>
            </Grid>

            {/* Quick Add Options */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.include_all_admins}
                    onChange={(e) => setEditFormData({ ...editFormData, include_all_admins: e.target.checked })}
                  />
                }
                label="Include all administrators"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.include_all_technicians}
                    onChange={(e) => setEditFormData({ ...editFormData, include_all_technicians: e.target.checked })}
                  />
                }
                label="Include all technicians"
              />
            </Grid>

            {/* Additional Users */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={users}
                value={users.filter(u => editFormData.additional_user_ids.includes(u.user?.id))}
                onChange={(_, newValue) => setEditFormData({
                  ...editFormData,
                  additional_user_ids: newValue.map(u => u.user?.id)
                })}
                getOptionLabel={(option) => {
                  const name = `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim()
                  return name || option.user?.username || ''
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Additional Users" placeholder="Select users..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.user?.id}
                      label={`${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.username}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>

            {/* Additional Departments */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={departments}
                value={departments.filter(d => editFormData.additional_department_ids.includes(d.id))}
                onChange={(_, newValue) => setEditFormData({
                  ...editFormData,
                  additional_department_ids: newValue.map(d => d.id)
                })}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Additional Departments" placeholder="Select departments..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
              />
              <Typography variant="caption" color="text.secondary">
                All users in selected departments will receive this notification
              </Typography>
            </Grid>

            {/* Additional Roles */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={roles}
                value={roles.filter(r => editFormData.additional_role_ids.includes(r.id))}
                onChange={(_, newValue) => setEditFormData({
                  ...editFormData,
                  additional_role_ids: newValue.map(r => r.id)
                })}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Additional Roles" placeholder="Select roles..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
              />
              <Typography variant="caption" color="text.secondary">
                All users with selected roles will receive this notification
              </Typography>
            </Grid>
          </Grid>
        )}
      </Modal>
    </>
  )
}

// System Credentials Tab (FCM, etc.)
const CredentialsTab = () => {
  const [credentials, setCredentials] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedCredential, setSelectedCredential] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    credential_type: 'fcm_service_account',
    description: '',
    credentials_json: null,
    is_active: true,
  })
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [fileName, setFileName] = useState('')

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await api.get('/common/credentials/')
      setCredentials(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching credentials:', error)
      if (error.response?.status === 403) {
        toast.error('You need admin privileges to manage credentials')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedCredential(null)
    setFormData({
      name: '',
      credential_type: 'fcm_service_account',
      description: '',
      credentials_json: null,
      is_active: true,
    })
    setFileName('')
    setTestResult(null)
    setFormOpen(true)
  }

  const handleEdit = async (credential) => {
    try {
      // Fetch full credential details including the JSON
      const response = await api.get(`/common/credentials/${credential.id}/`)
      const fullCredential = response.data
      setSelectedCredential(fullCredential)
      setFormData({
        name: fullCredential.name,
        credential_type: fullCredential.credential_type,
        description: fullCredential.description || '',
        credentials_json: fullCredential.credentials_json,
        is_active: fullCredential.is_active,
      })
      setFileName('Current credentials loaded')
      setTestResult(null)
      setFormOpen(true)
    } catch (error) {
      toast.error('Failed to load credential details')
    }
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a JSON file')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result)
        // Validate FCM service account fields
        const requiredFields = ['type', 'project_id', 'private_key', 'client_email']
        const missing = requiredFields.filter(f => !json[f])
        if (missing.length > 0) {
          toast.error(`Invalid FCM service account JSON. Missing: ${missing.join(', ')}`)
          return
        }
        setFormData(prev => ({ ...prev, credentials_json: json }))
        setFileName(file.name)
        setTestResult(null)
        toast.success('Service account JSON loaded successfully')
      } catch (err) {
        toast.error('Invalid JSON file')
      }
    }
    reader.readAsText(file)
  }

  const handleTest = async () => {
    if (!selectedCredential?.id) {
      toast.error('Save the credential first before testing')
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const response = await api.post(`/common/credentials/${selectedCredential.id}/test/`)
      setTestResult({ success: true, message: response.data.message, project_id: response.data.project_id })
      toast.success('FCM credentials are valid!')
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Test failed'
      setTestResult({ success: false, message: errorMsg })
      toast.error(errorMsg)
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Name is required')
      return
    }
    if (!formData.credentials_json) {
      toast.error('Please upload a service account JSON file')
      return
    }

    setSaving(true)
    try {
      if (selectedCredential) {
        await api.put(`/common/credentials/${selectedCredential.id}/`, formData)
        toast.success('Credential updated successfully')
      } else {
        await api.post('/common/credentials/', formData)
        toast.success('Credential created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving credential:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.credentials_json?.[0]
        || error.response?.data?.name?.[0]
        || 'Failed to save credential'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedCredential) return
    try {
      await api.delete(`/common/credentials/${selectedCredential.id}/`)
      toast.success('Credential deleted')
      setFormOpen(false)
      fetchData()
    } catch (error) {
      toast.error('Failed to delete credential')
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'credential_type_display', headerName: 'Type' },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      renderCell: (value) => new Date(value).toLocaleString(),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  const fcmCredential = credentials.find(c => c.credential_type === 'fcm_service_account' && c.is_active)

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Upload your Firebase Cloud Messaging (FCM) service account JSON to enable push notifications.
        You can download this from the Firebase Console under Project Settings → Service Accounts.
      </Alert>

      {!fcmCredential && credentials.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No FCM credentials configured. Push notifications will not work until you add your Firebase service account.
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Credential</Button>
      </Box>

      <DataTable
        columns={columns}
        data={credentials}
        loading={loading}
        searchPlaceholder="Search credentials..."
        emptyMessage="No credentials configured"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedCredential ? 'Edit Credential' : 'Add FCM Credential'}
        maxWidth="sm"
        actions={
          <>
            {selectedCredential && (
              <Button variant="outlined" color="error" onClick={handleDelete} sx={{ mr: 'auto' }}>
                Delete
              </Button>
            )}
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedCredential ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Production FCM"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Type"
              value={formData.credential_type}
              onChange={(e) => setFormData({ ...formData, credential_type: e.target.value })}
            >
              <MenuItem value="fcm_service_account">Firebase Cloud Messaging</MenuItem>
              <MenuItem value="smtp">SMTP Configuration</MenuItem>
              <MenuItem value="sms_gateway">SMS Gateway</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              placeholder="Optional description"
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: formData.credentials_json ? 'success.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: formData.credentials_json ? 'success.50' : 'background.default',
              }}
            >
              <input
                type="file"
                accept=".json"
                id="fcm-file-upload"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <label htmlFor="fcm-file-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                >
                  Upload Service Account JSON
                </Button>
              </label>
              {fileName && (
                <Typography variant="body2" color="success.main" mt={1}>
                  {fileName}
                </Typography>
              )}
              {formData.credentials_json?.project_id && (
                <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                  Project ID: {formData.credentials_json.project_id}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
          {selectedCredential && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleTest}
                  loading={testing}
                  disabled={testing}
                >
                  Test Connection
                </Button>
                {testResult && (
                  <Box display="flex" alignItems="center" gap={1}>
                    {testResult.success ? (
                      <CheckCircle color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                    <Typography
                      variant="body2"
                      color={testResult.success ? 'success.main' : 'error.main'}
                    >
                      {testResult.message}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Modal>
    </>
  )
}

// Settings Tab with Sub-tabs
const SettingsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState(0)

  return (
    <>
      <Tabs
        value={activeSubTab}
        onChange={(_, v) => setActiveSubTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab icon={<Notifications />} iconPosition="start" label="Notifications" />
        <Tab icon={<Security />} iconPosition="start" label="Credentials" />
      </Tabs>

      {activeSubTab === 0 && <NotificationSettingsTab />}
      {activeSubTab === 1 && <CredentialsTab />}
    </>
  )
}

// Main Administration Page
const Administration = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Determine active tab based on URL
  const activeTab = useMemo(() => {
    if (location.pathname.includes('/administration/departments')) return 1
    if (location.pathname.includes('/administration/roles')) return 2
    if (location.pathname.includes('/administration/settings')) return 3
    return 0 // Default to users
  }, [location.pathname])

  const handleTabChange = (_, newValue) => {
    const routes = ['/administration/users', '/administration/departments', '/administration/roles', '/administration/settings']
    navigate(routes[newValue])
  }

  // Redirect non-admins
  if (!user?.is_superuser && !user?.is_staff) {
    return (
      <Box>
        <PageHeader
          title="Administration"
          subtitle="Access denied"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Administration' },
          ]}
        />
        <Card>
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              You need administrator privileges to access this section.
            </Typography>
          </Box>
        </Card>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Administration"
        subtitle="Manage users, departments, roles, and settings"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Administration' },
        ]}
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Person />} iconPosition="start" label="Users" />
          <Tab icon={<Business />} iconPosition="start" label="Departments" />
          <Tab icon={<Security />} iconPosition="start" label="Roles" />
          <Tab icon={<Settings />} iconPosition="start" label="Settings" />
        </Tabs>

        {activeTab === 0 && <UsersTab />}
        {activeTab === 1 && <DepartmentsTab />}
        {activeTab === 2 && <RolesTab />}
        {activeTab === 3 && <SettingsTab />}
      </Card>
    </Box>
  )
}

export default Administration
